// src/Pages/Dealers.jsx
import React, { useEffect, useMemo, useState } from "react";
import Template from "../Components/Template";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MdDelete,
  MdEdit,
  MdInfo,
  MdOutlineNavigateNext,
  MdSkipNext,
  MdSkipPrevious,
} from "react-icons/md";
import { GrFormPrevious } from "react-icons/gr";
import useDataTable from "../hooks/useDataTable";
import {
  useAddDealerMutation,
  useDeleteDealerMutation,
  useGetAllDealersQuery,
  useUpdateDealerByIdMutation,
} from "../Services/modules/dealer";
import { useNavigate } from "react-router-dom";

function Dealers() {
  const navigate = useNavigate();

  const {
    handleFirstPage,
    handlePageSizeChange,
    handleLastPage,
    page,
    rowsPerPage,
    setTotalRows,
    setPage,
    setTotalPages,
    totalPages,
  } = useDataTable();

  // ====== Local State (no react-hook-form per your admin convention) ======
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  // scalar fields (non-files)
  const [dealer, setDealer] = useState({
    _id: "",
    dealerId: "",
    dealerName: "",
    dealerShopName: "",
    alternateContactNumber: "",
    whatsappNumber: "",
    permanentAddress: "",
    addressAtPost: "",
    addressTaluka: "",
    addressDistrict: "",
    addressState: "",
    addressPin: "",
    godownAddress: "",
    godownCapacity: "",
    irrigationStatus: "",
    majorCrops: "",
    email: "",
    gstNo: "",
    panNo: "",
    bankName: "",
    bankBranch: "",
    bankAccountNo: "",
    bankIfsc: "",
  });

  // file fields kept separate (FileList or File[])
  const [files, setFiles] = useState({
    dealerPhoto: null,
    blankChequePhoto: null,
    aadharCardPhoto: null,
    pesticideLicensePhoto: null,
    fertiliserLicensePhoto: null,
    selfDeclarationPhoto: null,
  });

  // ====== RTK Query Hooks ======
  // fetch all; we'll paginate on client
  const {
    data: dealersResp,
    isLoading: isLoadingDealers,
    refetch,
  } = useGetAllDealersQuery();

  // memoized list (array of dealers)
  const allDealers = useMemo(() => dealersResp?.data || [], [dealersResp]);

  const [addDealer, addMeta] = useAddDealerMutation();
  const [updateDealer, updateMeta] = useUpdateDealerByIdMutation();
  const [deleteDealer, deleteMeta] = useDeleteDealerMutation();

  // ====== Client-side pagination: derive totals from allDealers ======
  useEffect(() => {
    const total = allDealers.length || 0;
    setTotalRows(total);
    const pages = Math.max(1, Math.ceil(total / rowsPerPage));
    setTotalPages(pages);
    // clamp page to range
    if (page > pages - 1) setPage(Math.max(0, pages - 1));
  }, [allDealers, rowsPerPage]);

  // Visible slice + index base
  const start = page * rowsPerPage;
  const end = start + rowsPerPage;
  const visibleDealers = allDealers.slice(start, end);
  const displayIndexBase = start; // for "#" column

  // ====== Effects (toasts) ======
  useEffect(() => {
    if (addMeta.isSuccess) {
      toast.success(addMeta.data?.message || "Dealer added");
      setIsSubmitting(false);
      refetch();
    }
    if (addMeta.isError) {
      toast.error(addMeta.error?.data?.message || "Failed to add dealer");
      setIsSubmitting(false);
    }
  }, [addMeta.isSuccess, addMeta.isError]);

  useEffect(() => {
    if (updateMeta.isSuccess) {
      toast.success(updateMeta.data?.message || "Dealer updated");
      clearForm();
      setIsUpdate(false);
      setIsSubmitting(false);
      refetch();
    }
    if (updateMeta.isError) {
      toast.error(updateMeta.error?.data?.message || "Failed to update dealer");
      setIsSubmitting(false);
    }
  }, [updateMeta.isSuccess, updateMeta.isError]);

  // ====== Helpers: file validation ======
  const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

  const anyFileTooLarge = (fileList) =>
    Array.from(fileList || []).some((f) => f.size > MAX_FILE_BYTES);

  const handleFileChange = (name, e) => {
    const fileList = e?.target?.files;
    if (!fileList) return;

    if (anyFileTooLarge(fileList)) {
      toast.error("One or more files exceed 5 MB. Please choose smaller files.");
      // reset the input & do NOT update state
      e.target.value = "";
      return;
    }

    // ok -> store in state
    setFiles((f) => ({ ...f, [name]: fileList }));
  };

  // ====== Handlers ======
  const handleChange = (name, value) => {
    setDealer((d) => ({ ...d, [name]: value }));
  };

  const clearForm = () => {
    setDealer({
      _id: "",
      dealerId: "",
      dealerName: "",
      dealerShopName: "",
      alternateContactNumber: "",
      whatsappNumber: "",
      permanentAddress: "",
      addressAtPost: "",
      addressTaluka: "",
      addressDistrict: "",
      addressState: "",
      addressPin: "",
      godownAddress: "",
      godownCapacity: "",
      irrigationStatus: "",
      majorCrops: "",
      email: "",
      gstNo: "",
      panNo: "",
      bankName: "",
      bankBranch: "",
      bankAccountNo: "",
      bankIfsc: "",
    });
    setFiles({
      dealerPhoto: null,
      blankChequePhoto: null,
      aadharCardPhoto: null,
      pesticideLicensePhoto: null,
      fertiliserLicensePhoto: null,
      selfDeclarationPhoto: null,
    });
  };

  const onSubmit = async () => {
    if (!dealer.dealerName?.trim()) {
      toast.error("Dealer name is required");
      return;
    }
    if (!dealer.alternateContactNumber || !/^\d{10}$/.test(dealer.alternateContactNumber)) {
      toast.error("Enter valid 10-digit Alternate Number");
      return;
    }
    if (!dealer.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dealer.email)) {
      toast.error("Enter a valid email");
      return;
    }

    // final safety: block submit if any selected file > 5 MB
    const allPicked = Object.values(files).filter(Boolean);
    const overLimit = allPicked.some((fl) => anyFileTooLarge(fl));
    if (overLimit) {
      toast.error("Cannot submit: at least one selected file exceeds 5 MB.");
      return;
    }

    setIsSubmitting(true);

    const fd = new FormData();

    // add scalar fields
    Object.entries(dealer).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, String(v));
    });

    // add file fields
    const appendFiles = (fieldName) => {
      const fl = files[fieldName];
      if (!fl) return;
      Array.from(fl).forEach((file) => fd.append(fieldName, file));
    };

    appendFiles("dealerPhoto");
    appendFiles("blankChequePhoto");
    appendFiles("aadharCardPhoto");
    appendFiles("pesticideLicensePhoto");
    appendFiles("fertiliserLicensePhoto");
    appendFiles("selfDeclarationPhoto");

    try {
      if (isUpdate && dealer._id) {
        await updateDealer(fd).unwrap();
      } else {
        await addDealer(fd).unwrap();
      }
    } catch {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setDealer({
      _id: row._id || "",
      dealerId: row.dealerId || "",
      dealerName: row.dealerName || "",
      dealerShopName: row.dealerShopName || "",
      alternateContactNumber: row.alternateContactNumber || "",
      whatsappNumber: row.whatsappNumber || "",
      permanentAddress: row.permanentAddress || "",
      addressAtPost: row.addressAtPost || "",
      addressTaluka: row.addressTaluka || "",
      addressDistrict: row.addressDistrict || "",
      addressState: row.addressState || "",
      addressPin: row.addressPin || "",
      godownAddress: row.godownAddress || "",
      godownCapacity: row.godownCapacity || "",
      irrigationStatus: row.irrigationStatus || "",
      majorCrops: row.majorCrops || "",
      email: row.email || "",
      gstNo: row.gstNo || "",
      panNo: row.panNo || "",
      bankName: row.bankName || "",
      bankBranch: row.bankBranch || "",
      bankAccountNo: row.bankAccountNo || "",
      bankIfsc: row.bankIfsc || "",
    });
    setFiles({
      dealerPhoto: null,
      blankChequePhoto: null,
      aadharCardPhoto: null,
      pesticideLicensePhoto: null,
      fertiliserLicensePhoto: null,
      selfDeclarationPhoto: null,
    });
    setIsUpdate(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInfo = (row) => {
    if (!row.dealerId) {
      toast.error("Dealer reference number missing");
      return;
    }
    navigate({
      pathname: "/dealerdetails",
      search: `?dealerId=${row.dealerId}`,
    });
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete dealer "${row.dealerName}" (Ref: ${row.dealerId})?`);
    if (!ok) return;
    try {
      await deleteDealer({ id: row._id, dealerId: row.dealerId }).unwrap();
      toast.success("Dealer deleted");
      refetch();
    } catch {
      toast.error(deleteMeta?.error?.data?.message || "Failed to delete dealer");
    }
  };

  // ====== UI ======
  const form = (
    <div className="col-md-12">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">{isUpdate ? "Update Dealer" : "Add Dealer"}</h5>

          {/* Basic Details */}
          <div className="form-group">
            <label>Dealer Name *</label>
            <input
              type="text"
              className="form-control mb-3"
              value={dealer.dealerName}
              onChange={(e) => handleChange("dealerName", e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Shop Name</label>
            <input
              type="text"
              className="form-control mb-3"
              value={dealer.dealerShopName}
              onChange={(e) => handleChange("dealerShopName", e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Alternate Number *</label>
            <input
              type="tel"
              className="form-control mb-3"
              value={dealer.alternateContactNumber}
              maxLength={10}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                handleChange("alternateContactNumber", v);
              }}
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              className="form-control mb-3"
              value={dealer.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>

          {/* WhatsApp (no OTP on admin) */}
          <div className="form-group">
            <label>WhatsApp Number</label>
            <input
              type="tel"
              className="form-control mb-3"
              value={dealer.whatsappNumber}
              maxLength={10}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                handleChange("whatsappNumber", v);
              }}
            />
          </div>

          {/* Address */}
          <h6 className="mt-3">Address</h6>
          <div className="form-group">
            <label>Permanent Address *</label>
            <input
              type="text"
              className="form-control mb-3"
              value={dealer.permanentAddress}
              onChange={(e) => handleChange("permanentAddress", e.target.value)}
            />
          </div>

          <div className="row">
            <div className="col-md-6 form-group">
              <label>At / Post *</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.addressAtPost}
                onChange={(e) => handleChange("addressAtPost", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Taluka *</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.addressTaluka}
                onChange={(e) => handleChange("addressTaluka", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>District *</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.addressDistrict}
                onChange={(e) => handleChange("addressDistrict", e.target.value)}
              />
            </div>
            <div className="col-md-4 form-group">
              <label>State *</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.addressState}
                onChange={(e) => handleChange("addressState", e.target.value)}
              />
            </div>
            <div className="col-md-2 form-group">
              <label>PIN *</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.addressPin}
                onChange={(e) => handleChange("addressPin", e.target.value)}
              />
            </div>
          </div>

          {/* Godown */}
          <h6 className="mt-3">Godown</h6>
          <div className="row">
            <div className="col-md-8 form-group">
              <label>Godown Address</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.godownAddress}
                onChange={(e) => handleChange("godownAddress", e.target.value)}
              />
            </div>
            <div className="col-md-4 form-group">
              <label>Capacity (MT)</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.godownCapacity}
                onChange={(e) => handleChange("godownCapacity", e.target.value)}
              />
            </div>
          </div>

          {/* Crops */}
          <h6 className="mt-3">Crops & Irrigation</h6>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>Major Crops</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.majorCrops}
                onChange={(e) => handleChange("majorCrops", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Irrigation Status</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.irrigationStatus}
                onChange={(e) => handleChange("irrigationStatus", e.target.value)}
              />
            </div>
          </div>

          {/* GST / PAN / Bank */}
          <h6 className="mt-3">GST / PAN / Bank</h6>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>GST No.</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.gstNo}
                onChange={(e) => handleChange("gstNo", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>PAN</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.panNo}
                onChange={(e) => handleChange("panNo", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Bank Name</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.bankName}
                onChange={(e) => handleChange("bankName", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Branch</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.bankBranch}
                onChange={(e) => handleChange("bankBranch", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Account No.</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.bankAccountNo}
                onChange={(e) => handleChange("bankAccountNo", e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>IFSC</label>
              <input
                type="text"
                className="form-control mb-3"
                value={dealer.bankIfsc}
                onChange={(e) => handleChange("bankIfsc", e.target.value)}
              />
            </div>
          </div>

          {/* Uploads (with 5MB validation) */}
          <h6 className="mt-3">Upload Documents</h6>
          <div className="row">
            <div className="col-md-6 form-group">
              <label>Dealer Photo (Max 5MB)</label>
              <input
                type="file"
                className="form-control mb-3"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("dealerPhoto", e)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Blank Cheque (Max 5MB)</label>
              <input
                type="file"
                className="form-control mb-3"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("blankChequePhoto", e)}
              />
            </div>

            <div className="col-md-6 form-group">
              <label>Aadhar (Front & Back) (Max 5MB each)</label>
              <input
                type="file"
                className="form-control mb-3"
                multiple
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("aadharCardPhoto", e)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Pesticide License (Max 5MB each)</label>
              <input
                type="file"
                className="form-control mb-3"
                multiple
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("pesticideLicensePhoto", e)}
              />
            </div>

            <div className="col-md-6 form-group">
              <label>Fertiliser License (Max 5MB each)</label>
              <input
                type="file"
                className="form-control mb-3"
                multiple
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("fertiliserLicensePhoto", e)}
              />
            </div>
            <div className="col-md-6 form-group">
              <label>Self Declaration (Max 5MB each)</label>
              <input
                type="file"
                className="form-control mb-3"
                multiple
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={(e) => handleFileChange("selfDeclarationPhoto", e)}
              />
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : isUpdate ? "Update" : "Submit"}
          </button>

          {isUpdate && (
            <button
              type="button"
              className="btn btn-secondary ms-2"
              onClick={() => {
                clearForm();
                setIsUpdate(false);
              }}
              disabled={isSubmitting}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const list = (
    <div className="col-md-12">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">All Dealers</h5>

          <table className="bordered-table table">
            <thead>
              <tr>
                <th>Action</th>
                <th>#</th>
                <th>Ref No</th>
                <th>Name</th>
                <th>Shop</th>
                <th>WhatsApp</th>
                <th>District</th>
              </tr>
            </thead>
            <tbody>
              {visibleDealers.map((item, idx) => (
                <tr key={item._id || `${item.dealerId}-${idx}`}>
                  <td className="d-flex gap-2 align-items-center">
                    <MdEdit
                      onClick={() => handleEdit(item)}
                      style={{ cursor: "pointer" }}
                      title="Edit"
                    />
                    <MdInfo
                      onClick={() => handleInfo(item)}
                      style={{ cursor: "pointer" }}
                      title="Info"
                    />
                    <MdDelete
                      onClick={() => handleDelete(item)}
                      style={{ cursor: "pointer", color: "#c0392b" }}
                      title="Delete"
                    />
                  </td>
                  <td>{displayIndexBase + idx + 1}</td>
                  <td>{item.dealerId}</td>
                  <td>{item.dealerName}</td>
                  <td>{item.dealerShopName}</td>
                  <td>{item.whatsappNumber}</td>
                  <td>{item.addressDistrict}</td>
                </tr>
              ))}

              {visibleDealers.length === 0 && !isLoadingDealers && (
                <tr>
                  <td colSpan={7} className="text-center py-3">
                    No dealers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Client-side pagination controls */}
          <div className="d-flex align-items-center gap-2">
            <button onClick={() => handleFirstPage()} disabled={page === 0} className="btn btn-light">
              <MdSkipPrevious />
            </button>
            <button onClick={() => setPage(page - 1)} disabled={page === 0} className="btn btn-light">
              <GrFormPrevious />
            </button>
            <div className="mx-2 small">
              Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong>
              {" • "}
              Rows per page:
            </div>
            <select value={rowsPerPage} onChange={handlePageSizeChange} className="form-select w-auto">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              className="btn btn-light ms-auto"
            >
              <MdOutlineNavigateNext />
            </button>
            <button
              onClick={() => handleLastPage()}
              disabled={page >= totalPages - 1}
              className="btn btn-light"
            >
              <MdSkipNext />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Template>
      <ToastContainer />
      <main id="main" className="main">
        <div className="container">
          <div className="row">{list}</div>
          <div className="row">{form}</div>
        </div>
      </main>
    </Template>
  );
}

export default Dealers;
